import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import classnames from 'classnames';
import Autosuggest from 'react-autosuggest';
import ModalSearchMask from 'ferries/Home/components/FerrySearchMask/Mobile/ModalSearchMask';
import PortSuggetion from 'ferries/Home/components/FerrySearchMask/Mobile/PortSuggetion';
import PortsCountries from 'ferries/Home/components/FerrySearchMask/PopularPortOverlay/PortsCountries';
import PopularPort from 'ferries/Home/components/FerrySearchMask/PopularPortOverlay/PopularPort';
import LastSearches from 'ferries/Home/components/FerrySearchMask/Mobile/LastSearches';
import {
  themePortModal,
  selectAll,
  getPopularPorts,
  transformQuery,
  renderPortSuggestion
} from 'ferries/Home/components/FerrySearchMask/helperStore';
import {
  getResultsBasedOnTypedLanguage, isGreekText
} from 'providers/ferries/autocompleteResultsBasedOnTypedLanguage/utils';

const showSuggestions = (value) => value.trim().length > 1;

const PortModal = ({
  type, port, isGreekDeparture, selected, placeholder, modalTitle, tripIndex, popularPorts,
  queries, portString, onSuggestionSelected, onChange, fetchSuggestions, clearSuggestions,
  setPortByThreeLetterCode, fillWithQuery, onChangePopularPortCountry, translations
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const portExists = Boolean(port);

  const {ports, filters} = popularPorts;

  const filteredPorts = getPopularPorts(10, isGreekDeparture)(ports);
  const nofPopularPorts = filteredPorts.length;

  const handleOpen = () => setIsOpen((isOpen) => !isOpen);
  const onClickPort = (locationAbbr, locationName) => {
    const isGreekLocation = isGreekText(locationName);
    setPortByThreeLetterCode(type, locationAbbr, tripIndex, true, isGreekLocation);
    handleOpen();
  };
  const onClickQuery = (query) => {
    fillWithQuery(transformQuery(query));
    handleOpen();
  };

  const valueClasses = classnames('text-truncate', {
    'text-muted': !portExists
  });

  const reducedPorts = getResultsBasedOnTypedLanguage({term: port, listOfTerms: selected});

  return (
    <>
      <div className={themePortModal.input} onClick={handleOpen}>
        <div className={valueClasses}>{port || placeholder}</div>
      </div>
      <ModalSearchMask
        show={isOpen}
        title={modalTitle}
        toggleModal={handleOpen}
        header={
          <Autosuggest
            id={`${type}-${tripIndex}`}
            theme={{...themePortModal}}
            inputProps={{
              type: 'search',
              placeholder: placeholder,
              value: port,
              onClick: selectAll,
              onChange: (ignore, {newValue}) => onChange(type, newValue, tripIndex)
            }}
            suggestions={reducedPorts}
            onSuggestionsFetchRequested={({value}) => fetchSuggestions(type, value, tripIndex)}
            onSuggestionsClearRequested={() => clearSuggestions(type, tripIndex)}
            getSuggestionValue={portString}
            renderSuggestion={renderPortSuggestion}
            shouldRenderSuggestions={showSuggestions}
            onSuggestionSelected={(event, {suggestion}) => {
              onSuggestionSelected(type, suggestion, tripIndex);
              handleOpen();
            }}
          />
        }
      >
        {
          portExists &&
            <div className="mb-4">
              {
                reducedPorts.map((port) =>
                  <PortSuggetion
                    key={port.ferryOwnLocationID + Math.random()}
                    {...port}
                    onClickPort={onClickPort}
                  />
                )
              }
            </div>
        }
        {
          !portExists && ports?.length > 0 &&
            <>
              {filters.countries.length > 1 && <PortsCountries
                showArrows
                popularPorts={popularPorts}
                onChangePopularPortCountry={onChangePopularPortCountry}
                translations={translations}
              />}
              <div>
                <h4 className='mb-4'>{translations.mostPopular}</h4>
                {filteredPorts.map((popularPort, index) =>
                  <PopularPort
                    key={popularPort.ferryOwnLocationID}
                    {...popularPort}
                    isLast={nofPopularPorts - 1 === index}
                    onClickPopularPort={onClickPort}
                  />
                )}
              </div>
            </>
        }
        {
          fillWithQuery && queries?.length > 0 &&
            <>
              <div className="my-3 two-border-top-dotted" />
              <LastSearches
                queries={queries}
                onClickQuery={onClickQuery}
                translations={translations}
              />
            </>
        }
      </ModalSearchMask>
    </>
  );
};

PortModal.propTypes = {
  type: PropTypes.string.isRequired,
  port: PropTypes.string.isRequired,
  isGreekDeparture: PropTypes.bool,
  selected: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  placeholder: PropTypes.string.isRequired,
  modalTitle: PropTypes.string.isRequired,
  tripIndex: PropTypes.number,
  popularPorts: PropTypes.object.isRequired,
  queries: PropTypes.object,
  portString: PropTypes.func.isRequired,
  onSuggestionSelected: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  fetchSuggestions: PropTypes.func.isRequired,
  clearSuggestions: PropTypes.func.isRequired,
  setPortByThreeLetterCode: PropTypes.func.isRequired,
  fillWithQuery: PropTypes.func,
  onChangePopularPortCountry: PropTypes.func.isRequired,
  translations: PropTypes.shape({
    mostPopular: PropTypes.string.isRequired
  })
};

export default observer(PortModal);
